import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Navigation, Footer, IntroOverlay } from '../components/layout';
import ScrollProgress from '../components/ScrollProgress';
import { PAGE_TO_PATH, pathToPage } from './routes';
import { applySeo } from './seo';
import '../styles/tokens.css';
import '../styles/base.css';

const HomePage = lazy(() => import('../pages/HomePage'));
const AboutPage = lazy(() => import('../pages/AboutPage'));
const ServicesPage = lazy(() => import('../pages/ServicesPage'));
const OutcomesPage = lazy(() => import('../pages/OutcomesPage'));
const ProcessPage = lazy(() => import('../pages/ProcessPage'));
const ContactPage = lazy(() => import('../pages/ContactPage'));

const INTRO_SEEN_KEY = 'ss-intro-seen-v1';

export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const shouldShowIntro = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seenIntro = window.sessionStorage.getItem(INTRO_SEEN_KEY) === '1';
    return !reducedMotion && !seenIntro;
  }, []);
  const [showIntro, setShowIntro] = useState(shouldShowIntro);
  const [introComplete, setIntroComplete] = useState(() => !shouldShowIntro);

  const currentPage = pathToPage(location.pathname);

  useEffect(() => {
    applySeo(location.pathname);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  const setCurrentPage = (page) => {
    const path = PAGE_TO_PATH[page] || '/';
    navigate(path);
  };

  const handleIntroComplete = () => {
    setShowIntro(false);
    setIntroComplete(true);
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(INTRO_SEEN_KEY, '1');
    }
  };

  return (
    <>
      <div className="grain" />
      {showIntro && <IntroOverlay onComplete={handleIntroComplete} />}
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <ScrollProgress />
      <main>
        <Suspense fallback={<div aria-hidden="true" style={{ minHeight: '1px' }} />}>
          <Routes>
            <Route path="/" element={<HomePage setCurrentPage={setCurrentPage} introComplete={introComplete} />} />
            <Route path="/about" element={<AboutPage setCurrentPage={setCurrentPage} />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/outcomes" element={<OutcomesPage setCurrentPage={setCurrentPage} />} />
            <Route path="/process" element={<ProcessPage setCurrentPage={setCurrentPage} />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
      <Footer setCurrentPage={setCurrentPage} />
    </>
  );
}
